import { useMemo } from "react";

import { BaseApp, Order } from "@web-app/types";

import { useGetAppsByIds } from "./useApps";

/**
 * Hook to batch fetch apps for a list of orders.
 * Extracts unique appIds from orders and fetches them in a single request.
 */
export const useOrderApps = (orders: Order[]) => {
  const ordersKey = useMemo(
    () =>
      orders
        .map((o) => `${o.id}:${o.appId || ""}`)
        .sort()
        .join(","),
    [orders],
  );

  const appIds = useMemo(() => {
    const ids = new Set<string>();
    orders.forEach((order) => {
      if (order.appId) {
        ids.add(order.appId);
      }
    });
    return Array.from(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersKey]);

  const { data: apps, isLoading, isError } = useGetAppsByIds(appIds);

  const appsMap = useMemo(() => {
    const map = new Map<string, BaseApp>();
    if (apps) {
      apps.forEach((app) => {
        map.set(app.id, app);
      });
    }
    return map;
  }, [apps]);

  return { appsMap, isError, isLoading };
};
